import { useEffect, useState } from "react";
import { Container, Spinner } from "reactstrap";
import ChatBot from "../helpers/chatbotNewWidget";
import fetchUserDetailsFromParamsOrCookie from "../helpers/token-util";
import { KEY_CHAIN } from "../constants";
import TrackSnowPlowEvents from "../helpers/trackSnowPlowEvents";
import useTaskDeviceBridge from "@docsapp/native-wizard-sa/src/lib/useTaskDeviceBridge";
import { getCookie } from "../helpers/utils";
import { TokenType } from "@docsapp/native-wizard-sa/src/lib/TaskDeviceBridge";
import { MIA_CHATBOT_URL } from "../helpers/chatbotNavigation";

function UpdatedWidget() {
  const [userDetailsFetched, setUserDetailsFetched] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [longToken, setLongToken] = useState("");
  const [shortToken, setShortToken] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const taskDeviceBridge = useTaskDeviceBridge();

  const fetchUserDetailsWithTokenRefresh = async () => {
    try {
      // First attempt with existing tokens
      const resp = await fetchUserDetailsFromParamsOrCookie();

      if (resp && resp.userInfo) {
        setUserDetailsFetched(true);
        setUserDetails(resp.userInfo);
        setLongToken(resp.longToken);
        setShortToken(resp.shortToken);
        setIsLoading(false);
        TrackSnowPlowEvents(resp.userInfo);
        return;
      }

      // If no user details, try native token refresh (only on Android/iOS)
      if (!resp || !resp.userInfo) {
        const deviceOs = getCookie("platform");
        const isNativePlatform =
          deviceOs === "Android_mv" || deviceOs === "iOS_mv";

        if (isNativePlatform) {
          try {
            // Call native token refresh - this just triggers refresh, doesn't return tokens

            const res = await taskDeviceBridge.fetchNewToken(
              TokenType.MARKETPLACE_SHORT_TOKEN
            );

            const tokenData = typeof res === "string" ? JSON.parse(res) : res;
            //We store the short token or mbServerToken in the redirect cookie, then identify the token type and use it.
            const token = tokenData?.redirect || tokenData?.mbServerToken;
            if (token) {
              document.cookie = `${KEY_CHAIN.REDIRECT}=${token};path=/`;
            } else {
              await new Promise((resolve) => setTimeout(resolve, 30));
            }

            // After successful refresh, get tokens from cookies again
            const refreshedResp = await fetchUserDetailsFromParamsOrCookie();

            if (refreshedResp && refreshedResp.userInfo) {
              setUserDetailsFetched(true);
              setUserDetails(refreshedResp.userInfo);
              setLongToken(refreshedResp.longToken);
              setShortToken(refreshedResp.shortToken);
              setIsLoading(false);
              TrackSnowPlowEvents(refreshedResp.userInfo);
              return;
            } else {
              redirectToChatbot();
              return;
            }
          } catch (refreshError) {
            redirectToChatbot();
            return;
          }
        } else {
          redirectToChatbot();
          return;
        }
      }

      // No tokens available or other error, set as failed
      setUserDetailsFetched(true);
      setUserDetails(null);
      setIsLoading(false);
      TrackSnowPlowEvents(null);
    } catch (error) {
      setUserDetailsFetched(true);
      setUserDetails(null);
      setIsLoading(false);
      TrackSnowPlowEvents(null);
    }
  };

  useEffect(() => {
    fetchUserDetailsWithTokenRefresh();

    // Detect embed mode
    const urlParams = new URLSearchParams(window.location.search);
    const isEmbedMode = urlParams.get("embed") === "true";
    
    if (isEmbedMode) {
      document.body.classList.add("embed-mode");
    }

    return () => {
      document.body.classList.remove("embed-mode");
    };
  }, []);

  const redirectToChatbot = () => {
    window.location.assign(MIA_CHATBOT_URL);
  };

  if (isLoading || !userDetailsFetched) {
    return (
      <main role="main" aria-label="Loading Support Chat">
        <Container>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100vh",
            }}
            role="status"
            aria-live="polite"
            aria-label="Loading support chat widget"
          >
            <Spinner color="primary" />
          </div>
        </Container>
      </main>
    );
  }

  return (
    <main role="main" aria-label="Customer Support Chat">
      <Container>
        <div>..</div> {/* Render dialog */}
        <section
          role="region"
          aria-label="Chat Widget"
        >
          {userDetailsFetched && (
            <ChatBot
              userDetails={userDetails}
              longToken={longToken}
              shortToken={shortToken}
            />
          )}
        </section>
      </Container>
    </main>
  );
}

export default UpdatedWidget;
